// MB.1 — the dye economy (D20). PURE (ENG-1). Two things live here:
//   1. the colour-reachability graph — which of the 16 dyes can actually be
//      produced from a set of raw materials, following primary sources and the
//      two-dye combination recipes. The recipes themselves are authored data
//      (tools/gen-dyes.ts); this graph is what the tests pin. black is now
//      rooted at `ink_sac` (dropped by the squid mob), which closes gray +
//      light_gray and the whole 16-colour space.
//   2. the concrete-powder → concrete water-contact rule, wired into the boot's
//      random-tick so coloured concrete is a genuine survival block, not just a
//      dropSelf placeholder.

export const DYE_COLORS = [
  'white', 'orange', 'magenta', 'light_blue', 'yellow', 'lime', 'pink', 'gray',
  'light_gray', 'cyan', 'purple', 'blue', 'brown', 'green', 'red', 'black',
] as const;
export type DyeColor = (typeof DYE_COLORS)[number];

/**
 * Primary dyes: colour → the single raw material that yields it, or null when no
 * material in the current game can produce it. `green` comes from *smelting*
 * cactus (a furnace recipe); the rest are one-ingredient crafts.
 */
export const DYE_SOURCES: Readonly<Record<DyeColor, string | null>> = {
  white: 'bone_meal', red: 'poppy', yellow: 'dandelion', blue: 'lapis_lazuli',
  green: 'cactus', brown: 'cocoa_beans',
  black: 'ink_sac', // ← squid drop; closes gray + light_gray (its two-dye chain)
  // secondaries have no direct material source:
  orange: null, magenta: null, light_blue: null, lime: null, pink: null,
  gray: null, light_gray: null, cyan: null, purple: null,
};

/** Secondary dyes: colour → the two dye colours mixed (shapeless) to make it. */
export const DYE_COMBOS: Readonly<Partial<Record<DyeColor, readonly [DyeColor, DyeColor]>>> = {
  orange: ['red', 'yellow'],
  lime: ['green', 'white'],
  pink: ['red', 'white'],
  light_blue: ['blue', 'white'],
  cyan: ['blue', 'green'],
  purple: ['blue', 'red'],
  magenta: ['purple', 'pink'],
  gray: ['black', 'white'],
  light_gray: ['gray', 'white'],
};

/**
 * Colours actually producible from a set of raw materials, by fixed-point over
 * the primary sources and the combination recipes. A combo is available once
 * both of its input colours are.
 */
export function reachableColors(materials: ReadonlySet<string>): Set<DyeColor> {
  const have = new Set<DyeColor>();
  let changed = true;
  while (changed) {
    changed = false;
    for (const color of DYE_COLORS) {
      if (have.has(color)) continue;
      const src = DYE_SOURCES[color];
      const combo = DYE_COMBOS[color];
      const ok =
        (src != null && materials.has(src)) ||
        (combo != null && have.has(combo[0]) && have.has(combo[1]));
      if (ok) {
        have.add(color);
        changed = true;
      }
    }
  }
  return have;
}

/** The overworld materials a fresh survival world can gather (used by the audit test).
 *  `ink_sac` is a squid drop, so all sixteen colours are now reachable. */
export const SURVIVAL_DYE_MATERIALS: ReadonlySet<string> = new Set([
  'bone_meal', 'poppy', 'dandelion', 'lapis_lazuli', 'cactus', 'cocoa_beans', 'ink_sac',
]);

/**
 * `<color>_dye` → its index in DYE_COLORS (which is the sheep/collar variant
 * order), or -1 if the item isn't a dye. Lets a dye recolour a sheep or a
 * wolf's collar without the host re-parsing colour names.
 */
export function dyeColorIndex(itemName: string): number {
  if (!itemName.endsWith('_dye')) return -1;
  return (DYE_COLORS as readonly string[]).indexOf(itemName.slice(0, -'_dye'.length));
}

/** `<color>_concrete_powder` → `<color>_concrete`, else null. */
export function concreteOf(name: string): string | null {
  if (!name.endsWith('_concrete_powder')) return null;
  const color = name.slice(0, -'_concrete_powder'.length);
  return (DYE_COLORS as readonly string[]).includes(color) ? `${color}_concrete` : null;
}

/** Concrete powder solidifies the instant any of its neighbours is water. */
export function solidifiesInWater(neighbourNames: readonly string[]): boolean {
  return neighbourNames.some((n) => n === 'water');
}
